import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtDateTime, fmtMoney, fmtNumber } from '../lib/format';
import { Empty, ErrorState, Ltr, SkeletonRows, useToast } from './ui';
import { useOpenDocument } from './Layout';

export type Delivery = {
  documentId: number;
  type: string;
  typeLabel: string;
  number: string | null;
  issueDate: string;
  total: number | null;
  customerId: number;
  customerName: string;
  phone: string | null;
  address: string | null;
  lines: number;
  deliveredAt: string | null;
  deliveredBy: string | null;
  receivedBy: string | null;
  orderId: number | null;
  daysOpen: number;
  pick?: { picked: number; lines: number; completed: boolean };
};
export type Preparing = { pick?: { picked: number; lines: number; completed: boolean }; orderId: number; typeLabel: string; customerId: number; customerName: string; phone: string | null; lines: number; total: number | null; prepNote: string | null; address: string | null; daysOpen: number };
type Deliveries = { state: 'open' | 'delivered'; from: string; openCount: number; items: Delivery[]; preparing: Preparing[] };

export const useOpenDeliveries = (customerId?: number) =>
  useQuery({ queryKey: ['deliveries', customerId ?? 'all', 'open'], queryFn: () => api<Deliveries>(`/api/orders/deliveries${qs({ customerId, state: 'open' })}`) });

/**
 * Open orders: invoices / delivery notes that went out, until ticked "סופק". Ticked ones move to
 * "סופקו" (last 60 days) and can be returned.
 */
export function DeliveriesTable({ customerId, showCustomer }: { customerId?: number; showCustomer?: boolean }) {
  const [state, setState] = useState<'open' | 'delivered'>('open');
  const { data, error, refetch, isLoading } = useQuery({
    queryKey: ['deliveries', customerId ?? 'all', state],
    queryFn: () => api<Deliveries>(`/api/orders/deliveries${qs({ customerId, state })}`),
  });
  const [busy, setBusy] = useState<number | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const openDoc = useOpenDocument();
  const navigate = useNavigate();

  const tick = async (d: Delivery, delivered: boolean) => {
    setBusy(d.documentId);
    try {
      await api(`/api/orders/deliveries/${d.documentId}`, { body: { delivered } });
      qc.invalidateQueries({ queryKey: ['deliveries'] });
      qc.invalidateQueries({ queryKey: ['workplan'] });
      toast.success(delivered ? `${d.customerName} — סומן כסופק` : 'הוחזר להזמנות הפתוחות');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  const items = data?.items ?? [];
  const preparing = state === 'open' ? (data?.preparing ?? []) : [];
  const cols = showCustomer ? 6 : 5;
  return (
    <>
      <div className="panel-head row wrap" style={{ gap: 10 }}>
        <h2>הזמנות פתוחות — לאספקה</h2>
        <div className="segmented" role="group" aria-label="מצב">
          <button className={state === 'open' ? 'active' : ''} onClick={() => setState('open')}>פתוחות{data && state === 'open' ? ` (${data.items.length + preparing.length})` : ''}</button>
          <button className={state === 'delivered' ? 'active' : ''} onClick={() => setState('delivered')}>סופקו</button>
        </div>
        <span className="spacer" />
        {state === 'open' && items.length + preparing.length ? (
          <span className="small muted">
            {preparing.length ? `${preparing.length} בהכנה · ` : ''}סה״כ {fmtMoney([...items, ...preparing].reduce((s, d) => s + (d.total ?? 0), 0), { round: true })}
          </span>
        ) : null}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>{state === 'open' ? 'סופק?' : 'סופק'}</th>
              {showCustomer ? <th>לקוח</th> : null}
              <th>מסמך</th>
              <th className="num">סכום</th>
              <th className="hide-sm">כתובת</th>
              <th className="hide-sm">{state === 'open' ? 'פתוחה' : 'מתי'}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows rows={4} cols={cols} />
            ) : items.length === 0 && preparing.length === 0 ? (
              <tr>
                <td colSpan={cols} style={{ height: 'auto' }}>
                  <Empty
                    title={state === 'open' ? 'אין הזמנות פתוחות' : 'עוד לא סומנו הזמנות כסופקו'}
                    body={state === 'open' ? `כל חשבונית מס, חשבונית מס/קבלה ותעודת משלוח שיוצאת (מ-${data ? fmtDate(data.from) : ''}) מופיעה כאן עד שמסמנים "סופק".` : undefined}
                  />
                </td>
              </tr>
            ) : (
              [
                ...preparing.map((p) => (
                  <tr key={`prep-${p.orderId}`} className="clickable" style={{ background: 'var(--accent-soft)' }} onClick={() => navigate(`/orders/${p.orderId}`)}>
                    <td><span className="badge warn">בהכנה</span></td>
                    {showCustomer ? (
                      <td className="primary">
                        <Link to={`/customers/${p.customerId}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ink)' }}>{p.customerName}</Link>
                        {p.phone ? <span className="sub"><Ltr>{p.phone}</Ltr></span> : null}
                      </td>
                    ) : null}
                    <td>
                      טיוטת {p.typeLabel} · {fmtNumber(p.lines)} שורות
                      <span className="sub" style={p.prepNote ? { color: 'var(--warn)' } : undefined}>{p.prepNote ?? 'עוד לא הופק מסמך'}</span>
                    </td>
                    <td className="num tabular strong">{fmtMoney(p.total)}</td>
                    <td className="hide-sm small">{p.address ?? <span className="faint">—</span>}</td>
                    <td className="hide-sm small" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="link-button" onClick={() => navigate(`/pick/order/${p.orderId}`)}>ליקוט{p.pick?.picked ? ` (${p.pick.completed ? '✓' : `${p.pick.picked}/${p.pick.lines}`})` : ''}</button>
                      {' · '}
                      <button type="button" className="link-button" onClick={() => navigate(`/orders/${p.orderId}`)}>המשך להפקה</button>
                      <span className="sub">{p.daysOpen === 0 ? 'מהיום' : `${fmtNumber(p.daysOpen)} ימים`}</span>
                    </td>
                  </tr>
                )),
                ...items.map((d) => (
                <tr key={d.documentId} className="clickable" style={state === 'open' && d.daysOpen >= 3 ? { background: 'var(--warn-soft)' } : undefined} onClick={() => openDoc(d.documentId)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <label className="check" style={{ justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={state === 'delivered'}
                        disabled={busy === d.documentId}
                        onChange={(e) => tick(d, e.target.checked)}
                        aria-label={state === 'open' ? `סימון ${d.typeLabel} ${d.number ?? ''} כסופק` : 'החזרה להזמנות הפתוחות'}
                      />
                    </label>
                  </td>
                  {showCustomer ? (
                    <td className="primary">
                      <Link to={`/customers/${d.customerId}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ink)' }}>{d.customerName}</Link>
                      {d.phone ? <span className="sub"><Ltr>{d.phone}</Ltr></span> : null}
                    </td>
                  ) : null}
                  <td>
                    {d.typeLabel} <Ltr className="tabular strong">{d.number ?? '—'}</Ltr>
                    <span className="sub">{fmtDate(d.issueDate)} · {fmtNumber(d.lines)} שורות</span>
                  </td>
                  <td className="num tabular strong">{fmtMoney(d.total)}</td>
                  <td className="hide-sm small">{d.address ?? <span className="faint">—</span>}</td>
                  <td className="hide-sm small">
                    {state === 'open' ? (
                      <button type="button" className="link-button" style={{ display: 'block' }} onClick={(e) => (e.stopPropagation(), navigate(d.orderId ? `/pick/order/${d.orderId}` : `/pick/document/${d.documentId}`))}>
                        ליקוט{d.pick?.picked ? ` (${d.pick.completed ? '✓ לוקט' : `${d.pick.picked}/${d.pick.lines}`})` : ''}
                      </button>
                    ) : null}
                    {state === 'open' ? (
                      <span style={d.daysOpen >= 3 ? { color: 'var(--warn)' } : undefined}>{d.daysOpen === 0 ? 'היום' : `${fmtNumber(d.daysOpen)} ימים`}</span>
                    ) : (
                      <span className="muted">
                        {d.deliveredAt ? fmtDateTime(d.deliveredAt) : ''}{d.deliveredBy ? ` · ${d.deliveredBy}` : ''}
                        {d.receivedBy ? (
                          <button type="button" className="link-button" style={{ display: 'block' }} onClick={(e) => (e.stopPropagation(), navigate(`/delivery/${d.documentId}`))}>
                            ✍ נחתם: {d.receivedBy}
                          </button>
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
                )),
              ]
            )}
          </tbody>
        </table>
      </div>
      {state === 'open' && items.length ? <div className="panel-foot small muted">מסמנים V כשהסחורה הגיעה ללקוח. הזמנה פתוחה 3 ימים ומעלה מסומנת בצהוב ומופיעה בסדר העבודה היומי.</div> : null}
    </>
  );
}
